import type {
  DataGridCell,
  DataGridColumn,
  DataGridCoreTable,
  DataGridRow,
  RowData,
} from '../types';

/**
 * The order columns are rendered in: pinned-start, then unpinned, then pinned-end.
 *
 * This exists because TanStack offers two orders and it is easy to reach for the wrong one.
 * `getVisibleLeafColumns()` is the *declared* order — its own docs call it "the column list most
 * row rendering code uses before pinning-specific partitioning" — while `getHeaderGroups()` has
 * the partition already applied. A table that reads one for its header and the other for its body
 * puts every header from the pin point onward above the wrong column, and gives every `<col>` to
 * the wrong column with it. Measured, not theorised: pinning a middle column left the "Client"
 * header sitting over the matter numbers.
 *
 * Pinning moves a column to the edge in the DOM, rather than leaving it in place to stick on
 * scroll. That is what "pin left" means to the person who chose it — the column is at the edge
 * immediately, not once they scroll far enough — and it is also what makes `getPinnedOffset`'s
 * sum of the preceding pinned columns the column's actual x position.
 */
export function visibleLeafColumnsInRenderOrder<TRow extends RowData>(
  table: DataGridCoreTable<TRow>,
): DataGridColumn<TRow>[] {
  return [
    ...table.getStartVisibleLeafColumns(),
    ...table.getCenterVisibleLeafColumns(),
    ...table.getEndVisibleLeafColumns(),
  ];
}

/** The same order, for one row's cells — `row.getAllCells()` is the declared order. */
export function visibleCellsInRenderOrder<TRow extends RowData>(
  row: DataGridRow<TRow>,
): DataGridCell<TRow>[] {
  return [
    ...row.getStartVisibleCells(),
    ...row.getCenterVisibleCells(),
    ...row.getEndVisibleCells(),
  ];
}

/**
 * Every leaf column id as the consumer declared them — the third order, and the one
 * `getAllLeafColumns()` is not.
 *
 * That method runs the ordering function before returning, so it already carries `columnOrder` and,
 * under `groupedColumnMode: 'remove'`, drops the grouped column entirely. Anything that reads it to
 * build a list it will write *back* to `columnOrder` therefore writes an id out of existence: the
 * grouped column's slot is lost, and TanStack appends an id it was never told about last, so
 * clearing the grouping returned that column to the far right instead of where it left.
 *
 * Read straight off the column tree here, which nothing filters, so the list is complete whatever
 * is grouped or hidden. `completeColumnOrder` in `reorder-target.ts` is what pairs it with the
 * stored order.
 */
export function declaredLeafColumnIds<TRow extends RowData>(
  table: DataGridCoreTable<TRow>,
): string[] {
  return table
    .getAllColumns()
    .flatMap((column) => column.getLeafColumns())
    .map((column) => column.id);
}
